// Foundation imports
import { AnalyticsHandler, default as AnalyticsHandlerModule } from '@Foundation/analyticsHandler';
import { clickoutside } from '@Foundation/directives/clickoutside/clickoutside';
import { recaptcha } from '@Foundation/directives/recaptcha/recaptcha';
import { smoothScroll } from '@Foundation/directives/smoothScroll/smoothScroll';
import { nojshandler } from '@Foundation/directives/NoJsHandler/noJsHandler';
import { background } from '@Foundation/directives/background/background';
import { ellipsis } from '@Foundation/directives/ellipsis/ellipsis';
import { srcset } from '@Foundation/directives/srcset/srcset';
import { lineclamp } from '@Foundation/directives/lineclamp/lineclamp';
import { breadcrumbMobileClamp } from '@Foundation/directives/breadcrumbMobileClamp/breadcrumbMobileClamp';
import { triggerChannelAdvisor } from '@Foundation/directives/triggerChannelAdvisor/triggerChannelAdvisor';
import { debug } from '@Foundation/env/env';
import { InputHandler } from '@Foundation/inputHandler';
import LoadScriptModule from '@Foundation/loadscript';
import MqHandler from '@Foundation/mqHandler/MqHandler';
import { KeySiteCore } from '@Foundation/sitecore';
import { VueModule } from '@Foundation/utilities';
import { lazyScript } from '@Foundation/directives/lazyScript/lazyScript';
import '@Foundation/wsf/wsf';
import { store } from './store';

// feature imports
import Jebbit from '@Feature/JebbitIntegration';
import Quiz from '@Feature/Quiz';
import Tab from '@Feature/Tab';
import Tiktok from '@Feature/Tiktok';
import LanguageSelect from '@Feature/LanguageSelect';
import ContactUsForm from '@Feature/ContactUsForm';
import ChannelAdvisorModal from '@Feature/ChannelAdvisorModal';
import BvTabbedNavigation from '@Feature/BvTabbedNavigation';
import AccordionGroup from '@Feature/AccordionGroup';
import ProductStickyHeader from '@Feature/ProductStickyHeader';
import Routine from '@Feature/Routine';
import Search from '@Feature/Search';
import Menu from '@Feature/Menu';
import SearchFilters from '@Feature/SearchFilters';
import LoadMoreArticlesSearch from '@Feature/LoadMoreArticles';
import LoadMoreArticles from '@Feature/LoadMoreArticles';
import Drawer from '@Feature/Drawer';
import Burger from '@Feature/Burger';
import Carousel from '@Feature/Carousel';
import SkipTo from '@Feature/SkipTo';
import RatingsAndReviews from '@Feature/RatingsAndReviews';
import ShareResults from '@Feature/ShareResults';
import StoreLocatorBanner from '@Feature/StoreLocator';
import SfmcContainers from '@Feature/SFMC';
import SfmcArticles from '@Feature/SFMC';
import SfmcProducts from '@Feature/SFMC';
import VtoButton from '@Feature/VirtualTryOn';
import VtoApp from '@Feature/VirtualTryOn';
import OneTrustCookieHelper from '@Feature/OneTrustCookieHelper';
import ColorPicker from '@Feature/ColorPicker';
import MikMak from '@Feature/MikMak';

// project imports
import '@Themes/index.scss';
import Vue from 'vue';
import './registerServiceWorker';

Vue.use(KeySiteCore);

MqHandler.getMqHandler({
  small: 768,
  medium: 1024,
});

AnalyticsHandler.getAnalyticsHandler(debug);
InputHandler.getInputHandler();

export default VueModule({
  asyncImports: [
    {
      module: () =>
        import(/* webpackChunkName: "NewsletterFormToaster" */ '@Feature/NewsletterForm'),
      components: ['NewsletterFormToaster'],
    },
    {
      module: () => import(/* webpackChunkName: "NewsletterModal" */ '@Feature/NewsletterModal'),
      components: ['NewsletterModal'],
    },
    {
      module: () => import(/* webpackChunkName: "NewsletterForm" */ '@Feature/NewsletterForm'),
      components: ['NewsletterForm'],
    },
    {
      module: () => import(/* webpackChunkName: "Tooltip" */ '@Feature/Tooltip'),
      components: ['Tooltip'],
    },
    {
      module: () => import(/* webpackChunkName: "TwoColumns" */ '@Feature/TwoColumns'),
      components: ['TwoColumns'],
    },
    {
      module: () =>
        import(/* webpackChunkName: "ExploreFolderMobile" */ '@Feature/ExploreFolderMobile'),
      components: ['ExploreFolderMobile'],
    },
    {
      module: () =>
        import(/* webpackChunkName: "BvTabbedNavigation" */ '@Feature/BvTabbedNavigation'),
      components: ['BvTabbedNavigation'],
    },
    {
      module: () => import(/* webpackChunkName: "InPageNavigation" */ '@Feature/InPageNavigation'),
      components: ['InPageNavigation'],
    },
    {
      module: () => import(/* webpackChunkName: "IePopup" */ '@Feature/IePopup'),
      components: ['IePopup'],
    },
    {
      module: () => import(/* webpackChunkName: "CountryModal" */ '@Feature/CountryModal'),
      components: ['CountryModal'],
    },
    {
      module: () =>
        import(/* webpackChunkName: "ImmersiveBenefits" */ '@Feature/ImmersiveBenefits'),
      components: ['ImmersiveBenefits'],
    },
    {
      module: () => import(/* webpackChunkName: "youtube-player" */ '@Feature/YoutubePlayer'),
      components: ['YoutubePlayer'],
    },
    {
      module: () => import(/* webpackChunkName: "StoreLocator" */ '@Feature/StoreLocator'),
      components: ['StoreLocator'],
    },
    {
      module: () => import(/* webpackChunkName: "SkincareFinder" */ '@Feature/SkincareFinder'),
      components: ['SkincareFinder'],
    },
    {
      module: () =>
        import(/* webpackChunkName: "SkincareNewsletter" */ '@Feature/SkincareNewsletter'),
      components: ['SkincareNewsletter'],
    },
    {
      module: () => import(/* webpackChunkName: "ProductGallery" */ '@Feature/ProductGallery'),
      components: ['ProductGallery'],
    },
    {
      module: () => import(/* webpackChunkName: "ProductSlider" */ '@Feature/ProductSlider'),
      components: ['ProductSlider'],
    },
    {
      module: () => import(/* webpackChunkName: "BenefitCard" */ '@Feature/BenefitCard'),
      components: ['BenefitCard'],
    },
    {
      module: () => import(/* webpackChunkName: "LoadMoreResults" */ '@Feature/LoadMoreResults'),
      components: ['LoadMoreResults'],
    },
    {
      module: () => import(/* webpackChunkName: "Modal" */ '@Feature/Modal'),
      components: ['Modal'],
    },
    {
      module: () =>
        import(/* webpackChunkName: "AccessibilityModal" */ '@Feature/AccessibilityModal'),
      components: ['AccessibilityModal'],
    },
    {
      module: () => import(/* webpackChunkName: "ProductResult" */ '@Feature/ProductResult'),
      components: ['ProductResult'],
    },
    {
      module: () => import(/* webpackChunkName: "CopyParallax" */ '@Feature/CopyParallax'),
      components: ['CopyParallax'],
    },
    {
      module: () => import(/* webpackChunkName: "Accordion" */ '@Feature/Accordion'),
      components: ['Accordion'],
    },
    {
      module: () => import(/* webpackChunkName: "VideoComponent" */ '@Feature/VideoComponent'),
      components: ['VideoComponent'],
    },
    {
      module: () =>
        import(/* webpackChunkName: "FilteredArticleGrid" */ '@Feature/FilteredArticleGrid'),
      components: ['FilteredArticleGrid'],
    },
    {
      module: () =>
        import(/* webpackChunkName: "FilteredProductGrid" */ '@Feature/FilteredProductGrid'),
      components: ['FilteredProductGrid'],
    },
  ],
  imports: [
    AnalyticsHandlerModule,
    LoadScriptModule,

    // foundation components

    // feature components
    Jebbit,
    Quiz,
    Tab,
    Tiktok,
    LanguageSelect,
    ContactUsForm,
    ChannelAdvisorModal,
    BvTabbedNavigation,
    AccordionGroup,
    ProductStickyHeader,
    Routine,
    SearchFilters,
    Search,
    Menu,
    LoadMoreArticlesSearch,
    LoadMoreArticles,
    Drawer,
    Burger,
    Carousel,
    SkipTo,
    RatingsAndReviews,
    ShareResults,
    StoreLocatorBanner,
    SfmcContainers,
    SfmcArticles,
    SfmcProducts,
    VtoButton,
    VtoApp,
    OneTrustCookieHelper,
    ColorPicker,
    MikMak,
  ],
  directives: {
    background,
    clickoutside,
    recaptcha,
    smoothScroll,
    srcset,
    lineclamp,
    ellipsis,
    nojshandler,
    triggerChannelAdvisor,
    breadcrumbMobileClamp,
    lazyScript,
  },
  components: {},
  filters: {},
  store,
});
